import { groupBy } from 'lodash';
import { Internal } from '@gleamer/types';
import { utilities, Types as CoreTypes } from '@cornerstonejs/core';
import { PRE_ANNOTATION_DETECTION_TYPE } from '../preAnnotations/bacResultToPredictions';

type PredictionInternal = Internal.PredictionInternal;
type DetectionInternal = Internal.DetectionInternal;

export type PredictionBySeriesInstanceUID = Record<
  string,
  PredictionInternal[]
>;

export type PredictionByStudyInstanceUID = Record<
  string,
  PredictionBySeriesInstanceUID
>;

export function groupByStudyAndSeries(
  predictions: Prediction[] | undefined
): PredictionByStudyInstanceUID {
  if (!predictions?.length) {
    return {};
  }

  const byStudyInstanceUID = groupBy(predictions, 'studyInstanceUID');

  const bySeriesInstanceUID = Object.entries(byStudyInstanceUID).reduce(
    (acc, [studyInstanceUID, predictions]) => {
      acc[studyInstanceUID] = groupBy(predictions, 'seriesInstanceUID');
      return acc;
    },
    {} as PredictionByStudyInstanceUID
  );

  return bySeriesInstanceUID;
}

export function is2DPrediction(prediction: PredictionInternal) {
  return !!prediction.sopInstanceUID;
}

/** A contour Detection (a Pre-annotation) rather than a box. */
export function isContourDetection(detection: { type: string }) {
  return detection.type === PRE_ANNOTATION_DETECTION_TYPE;
}

export function is2DDetection(detection: DetectionInternal) {
  return detection.points.every((point) => !point.z);
}

export function is2DPointVisible(
  point: { x: number; y: number },
  viewport: CoreTypes.IStackViewport | CoreTypes.IVolumeViewport,
  imageId: string
) {
  const [x, y, z] = utilities.imageToWorldCoords(imageId, [point.x, point.y]);
  return is3DPointVisible({ x, y, z }, viewport);
}

export function is3DPointVisible(
  point: { x: number; y: number; z: number },
  viewport: CoreTypes.IStackViewport | CoreTypes.IVolumeViewport
) {
  const [x, y] = viewport.worldToCanvas([point.x, point.y, point.z]);

  return (
    x >= 0 &&
    x <= viewport.canvas.offsetWidth &&
    y >= 0 &&
    y <= viewport.canvas.offsetHeight
  );
}
